import { useEffect, useState } from "react";
import { FieldError, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { CircularProgress } from "@mui/material";
import { useAxios } from "../../utils/useAxios";
import { useFormData } from "../../context/FormDataContext";
import { useClickOutside } from "../../hooks/useClickOutside";
import { useData } from "../../context/GetDataContext";

interface MyCreateSelectProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string;
  setValue: UseFormSetValue<any>;
  watch?: UseFormWatch<any>;
  error?: FieldError;
  selected?: any;
  createName?: string;
  labelKey?: string;
  valueKey?: string;
  searching_key?: string;
  display_searching_key?: string;
  depend_key_name?: any;
  options_list: any;
}

export function MyCreateSelectSearch({
  name,
  setValue,
  error,
  selected,
  options_list,
  watch,
  createName,
  depend_key_name,
  labelKey = "factoring_name",
  valueKey = "id",
  searching_key = "id",
  display_searching_key = "name",
  ...rest
}: MyCreateSelectProps) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<any[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [displayValue, setDisplayValue] = useState("");
  const [resetValue, setResetValue] = useState("");

  const { formData } = useFormData();
  const { data } = useData();
  const { delivery_date, pick_up_date } = data;

  const wrapperRef = useClickOutside(() => setShowDropdown(false));

  const {
    res: searchRes,
    mutate: searchMutate,
    isPending: searchIsPending,
  } = useAxios({ url: "/api/auth/searchname" });

  // Fetch suggestions when formData or query changes
  useEffect(() => {
    const delay = setTimeout(() => {
      if (depend_key_name && Object.keys(formData)?.length) {
        const formKey = Object.keys(formData)[0];
        const id = formData[formKey];

        const mergedPayload = depend_key_name.map((item: any) => ({
          ...item,
          id,
          ...(displayValue && {
            name: displayValue,
          }),
          ...(pick_up_date &&
            delivery_date && {
              startDate: pick_up_date,
              endDate: delivery_date,
            }),
        }));

        searchMutate(mergedPayload);
      }
    }, 300);

    return () => clearTimeout(delay);
  }, [formData, query]);

  useEffect(() => {
    setOptions(searchRes?.data?.list || []);
  }, [searchRes]);

  useEffect(() => {
    if (watch) {
      const getid = watch(name);
      const matchdata = options_list.find((opt: any) => {
        return opt.value === getid;
      });
      setResetValue(matchdata?.label);
    }
  }, [watch?.(name), options_list]);

  return (
    <div className="relative" ref={wrapperRef}>
      <input
        autoComplete="off"
        value={displayValue || resetValue}
        onClick={() => setShowDropdown(true)}
        onChange={(e) => {
          setResetValue("");
          setDisplayValue(e.target.value);
          setQuery(e.target.value);
          setShowDropdown(true);
        }}
        className="h-7 w-full border px-3 py-1 text-sm shadow-theme-xs placeholder:text-gray-400 focus:outline-none focus:ring bg-transparent text-gray-800 border-gray-300 focus:border-brand-300 focus:ring focus:ring-brand-500/10"
        {...rest}
      />

      {showDropdown && (
        <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 shadow-lg max-h-60 overflow-y-auto">
          {searchIsPending ? (
            <li className="p-2 text-sm text-center">
              <CircularProgress size={16} />
            </li>
          ) : options?.length > 0 ? (
            options.map((option) => {
              const displayFields = Object.keys(
                depend_key_name?.[0]?.select || {},
              )
                .filter((field) => field !== "id")
                .sort();

              const list = displayFields
                .map((field) => option[field])
                .filter(Boolean)
                .join(" ");

              return (
                <li
                  key={option?.id}
                  onClick={() => {
                    setDisplayValue(list);
                    setValue(name, option.id);
                    setShowDropdown(false);
                  }}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                >
                  {list}
                </li>
              );
            })
          ) : (
            <li className="px-4 py-2 text-red-500 hover:bg-red-100 cursor-pointer text-sm">
              Not Found
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
